import React, { useRef, useState } from 'react';
import { Dropdown } from '../dropdown/Dropdown';
import { DropdownItem } from '../dropdown/DropdownItem';
import Button from '../button/Button';
import { useTableColumns } from '@/shared/hooks/useTableColumns';

export interface Column {
  key: string;
  header: string;
  field: string;
}

interface ColumnFilterProps {
  size?: 'sm' | 'md' | 'lg';
  buttonText?: string;
  className?: string;
}

/**
 * Компонент для фильтрации столбцов таблицы
 *
 * Позволяет пользователю выбирать, какие столбцы отображать в таблице.
 * Может быть размещен в любом месте внутри контекста TableColumnsProvider.
 *
 * @example
 * ```tsx
 * // Базовое использование
 * <ColumnFilter />
 *
 * // С настройкой размера и текста
 * <ColumnFilter size="sm" buttonText="Показать столбцы" />
 * ```
 */
export const ColumnFilter: React.FC<ColumnFilterProps> = ({
  size = 'sm',
  buttonText = 'Columns',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { allColumns, visibleColumns, toggleColumnVisibility } = useTableColumns();
  const prependDoubleClickRef = useRef(false);

  function handleButtonClick() {
    if (!prependDoubleClickRef.current && !isOpen) {
      setIsOpen(true);
    }
  }

  function handleClose() {
    setIsOpen(false);
    prependDoubleClickRef.current = true;
    setTimeout(() => {
      prependDoubleClickRef.current = false;
    }, 100);
  }

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="outline"
        size={size}
        onClick={handleButtonClick}
        className="w-fit flex items-center justify-between text-gray-700"
      >
        <div className="flex items-center gap-2 relative">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-gray-500"
          >
            <path
              d="M9 2C9 2.55228 8.55228 3 8 3C7.44772 3 7 2.55228 7 2C7 1.44772 7.44772 1 8 1C8.55228 1 9 1.44772 9 2Z"
              fill="currentColor"
            />
            <path
              d="M9 6C9 6.55228 8.55228 7 8 7C7.44772 7 7 6.55228 7 6C7 5.44772 7.44772 5 8 5C8.55228 5 9 5.44772 9 6Z"
              fill="currentColor"
            />
            <path
              d="M9 10C9 10.5523 8.55228 11 8 11C7.44772 11 7 10.5523 7 10C7 9.44772 7.44772 9 8 9C8.55228 9 9 9.44772 9 10Z"
              fill="currentColor"
            />
            <path
              d="M9 14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14C7 13.4477 7.44772 13 8 13C8.55228 13 9 13.4477 9 14Z"
              fill="currentColor"
            />
            <path
              d="M9 18C9 18.5523 8.55228 19 8 19C7.44772 19 7 18.5523 7 18C7 17.4477 7.44772 17 8 17C8.55228 17 9 17.4477 9 18Z"
              fill="currentColor"
            />
            <path
              d="M9 22C9 22.5523 8.55228 23 8 23C7.44772 23 7 22.5523 7 22C7 21.4477 7.44772 21 8 21C8.55228 21 9 21.4477 9 22Z"
              fill="currentColor"
            />
            <path
              d="M13 2C13 2.55228 12.5523 3 12 3C11.4477 3 11 2.55228 11 2C11 1.44772 11.4477 1 12 1C12.5523 1 13 1.44772 13 2Z"
              fill="currentColor"
            />
            <path
              d="M13 6C13 6.55228 12.5523 7 12 7C11.4477 7 11 6.55228 11 6C11 5.44772 11.4477 5 12 5C12.5523 5 13 5.44772 13 6Z"
              fill="currentColor"
            />
            <path
              d="M13 10C13 10.5523 12.5523 11 12 11C11.4477 11 11 10.5523 11 10C11 9.44772 11.4477 9 12 9C12.5523 9 13 9.44772 13 10Z"
              fill="currentColor"
            />
            <path
              d="M13 14C13 14.5523 12.5523 15 12 15C11.4477 15 11 14.5523 11 14C11 13.4477 11.4477 13 12 13C12.5523 13 13 13.4477 13 14Z"
              fill="currentColor"
            />
            <path
              d="M13 18C13 18.5523 12.5523 19 12 19C11.4477 19 11 18.5523 11 18C11 17.4477 11.4477 17 12 17C12.5523 17 13 17.4477 13 18Z"
              fill="currentColor"
            />
            <path
              d="M13 22C13 22.5523 12.5523 23 12 23C11.4477 23 11 22.5523 11 22C11 21.4477 11.4477 21 12 21C12.5523 21 13 21.4477 13 22Z"
              fill="currentColor"
            />
            <path
              d="M17 2C17 2.55228 16.5523 3 16 3C15.4477 3 15 2.55228 15 2C15 1.44772 15.4477 1 16 1C16.5523 1 17 1.44772 17 2Z"
              fill="currentColor"
            />
            <path
              d="M17 6C17 6.55228 16.5523 7 16 7C15.4477 7 15 6.55228 15 6C15 5.44772 15.4477 5 16 5C16.5523 5 17 5.44772 17 6Z"
              fill="currentColor"
            />
            <path
              d="M17 10C17 10.5523 16.5523 11 16 11C15.4477 11 15 10.5523 15 10C15 9.44772 15.4477 9 16 9C16.5523 9 17 9.44772 17 10Z"
              fill="currentColor"
            />
            <path
              d="M17 14C17 14.5523 16.5523 15 16 15C15.4477 15 15 14.5523 15 14C15 13.4477 15.4477 13 16 13C16.5523 13 17 13.4477 17 14Z"
              fill="currentColor"
            />
            <path
              d="M17 18C17 18.5523 16.5523 19 16 19C15.4477 19 15 18.5523 15 18C15 17.4477 15.4477 17 16 17C16.5523 17 17 17.4477 17 18Z"
              fill="currentColor"
            />
            <path
              d="M17 22C17 22.5523 16.5523 23 16 23C15.4477 23 15 22.5523 15 22C15 21.4477 15.4477 21 16 21C16.5523 21 17 21.4477 17 22Z"
              fill="currentColor"
            />
          </svg>
          <span className="py-[6px]">{buttonText}</span>
        </div>
        <svg
          className="w-4 h-4 text-gray-500 ml-2"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>

      <Dropdown
        isOpen={isOpen}
        onClose={handleClose}
        className="py-2 w-full z-50"
      >
        {allColumns.map(column => (
          <DropdownItem
            key={column.key}
            onClick={() => toggleColumnVisibility(column.key)}
            className="column-filter-item flex items-center gap-2 px-4 py-2"
          >
            <input
              type="checkbox"
              checked={visibleColumns.includes(column.key)}
              onChange={() => toggleColumnVisibility(column.key)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              data-testid={column.key}
            />
            <span className="text-text-secondary">{column.header}</span>
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  );
};
